import { t } from "adapters/i18n";
import type { AnimalCategory } from "shared/types";
import type { DataResult, ServiceConstructor } from "../services.types";
import numberUtils from "shared/numberUtils";

export function CategoryServiceMixin<TBase extends ServiceConstructor>(
  Base: TBase
) {
  return class CategoryService extends Base {
    readonly categoryUrl = `${process.env.REACT_APP_ENDPOINT}herds/animalCategory`;

    async fetchFarmCategories(
      farmId: number,
      withDeleted = true
    ): Promise<DataResult<AnimalCategory>> {
      try {
        const urlParams = this.montaURLParametros({ farmId, withDeleted });
        const url = this.montaURL(this.categoryUrl, urlParams);
        const result = await this.get(url);

        if (result.data?.data == null) {
          return { data: [], total: 0 };
        }

        const data = result.data.data as AnimalCategory[];
        const total = result.data.total as number;

        /**
         * API returns some weightGainCurveId as 0, but they should be treated as null
         */
        const parsedData = data.map<AnimalCategory>((item) => ({
          ...item,
          weightGainCurveId: numberUtils.parseValidNonZero(
            item.weightGainCurveId
          ),
        }));

        return { data: parsedData, total };
      } catch (_) {
        throw new Error(t("animalCategory:fetch-categories-error"));
      }
    }

    async createCategory(
      payload: Pick<
        AnimalCategory,
        | "ageRangeId"
        | "desiredNumber"
        | "farmId"
        | "gender"
        | "initialNumberConference"
        | "name"
        | "weightGainCurveId"
      >
    ): Promise<AnimalCategory> {
      try {
        const result = await this.post(this.categoryUrl, payload);
        return result.data;
      } catch (_) {
        throw new Error(t("animalCategory:create-category-error"));
      }
    }

    async deleteCategoryById(id: number): Promise<void> {
      try {
        await this.delete(`${this.categoryUrl}/${id}`);
      } catch (_) {
        throw new Error(t("animalCategory:delete-category-error"));
      }
    }

    async activateCategory(id: number): Promise<void> {
      try {
        await this.patch(`${this.categoryUrl}/${id}/activate`);
      } catch (_) {
        throw new Error(t("animalCategory:activate-category-error"));
      }
    }

    async updateCategory(
      id: number,
      payload: Partial<
        Pick<
          AnimalCategory,
          | "ageRangeId"
          | "desiredNumber"
          | "gender"
          | "initialNumberConference"
          | "name"
          | "weightGainCurveId"
        >
      >
    ): Promise<AnimalCategory> {
      try {
        const result = await this.patch(`${this.categoryUrl}/${id}`, payload);
        return result.data;
      } catch (_) {
        throw new Error(t("animalCategory:update-category-error"));
      }
    }
  };
}
